import type { ReactNode } from "react";

type Item = {
    title: string;
    /** Plain text, or JSX when a body needs inline links (e.g. citations
     *  to operator/official sites). */
    body: ReactNode;
    marker?: string;
    /** Per-item marker colour override — e.g. real toll-lane signage
     *  colours in a `highlight` grid, where each card needs its own
     *  marker fill rather than sharing one colour. */
    markerBg?: string;
    markerColor?: string;
};
type Country = { flag: string; name: string; body: string };
type Tool = {
    name: string;
    url?: string;
    cost: string;
    tagline: string;
    strengths: string;
    weaknesses: string;
    bestFor: string;
};
type Column = { heading: string; items: Item[] };
type Stat = { value: string; label: string };
type CategoryAccent = { fill: string; chipBg: string; chipText: string };
type DayMeta = {
    /** Number chip content — usually "01".."12", but left free-form. */
    catnum: string;
    flag: string;
    place: string;
    title: string;
    summary: string;
    accent: CategoryAccent;
};

type Props = {
    /** 1-based position among a guide's sections — drives the "01", "02"…
     *  number chip and the alternating band background. */
    index: number;
    /** Overrides the visible "01"/"02" chip text — use when a section's
     *  displayed number shouldn't match its alternation `index` (e.g. a
     *  highlighted section slotted in between two numbered ones without
     *  taking a number itself). */
    catnum?: string;
    heading?: string;
    intro?: string;
    items?: Item[];
    /** Renders a country-card grid instead of the numbered item rail. */
    countries?: Country[];
    /** Renders a tool-comparison rail (category aside + tool rows) instead
     *  of the plain numbered item rail. */
    tools?: Tool[];
    /** Renders independent mini-lists side by side (e.g. offline truths +
     *  file formats), each with its own heading — no shared aside. */
    columns?: Column[];
    /** Renders a day-rail: number chip + place chip + title + one-line
     *  summary on the left, free-form `children` (narrative, photos,
     *  callouts) on the right. */
    day?: DayMeta;
    children?: ReactNode;
    /** Caption for a striped placeholder box in the aside, marking where
     *  a real photo should go. */
    photoPlaceholder?: string;
    /** A real photo shown in the aside (below the heading/intro) — the
     *  shipped version of `photoPlaceholder`. Set at most one of the two. */
    photo?: { src: string; alt: string };
    /** Category accent for the `tools` variant — colours the number chip
     *  and the tinted eyebrow chip instead of the default teal/cream
     *  alternation. */
    accent?: CategoryAccent;
    /** Eyebrow chip text shown under the number chip (`tools` variant). */
    chipLabel?: string;
    /** Overrides the numeric "01"/"02" chip with a glyph (e.g. "★" for a
     *  highlighted section) and lays `items` (or `stats`) out full-width
     *  instead of a single list, with no left-hand aside. */
    highlight?: { glyph: string; background: string; color: string };
    /** Renders a grid of simple value/label stat cards instead of `items`
     *  — used with `highlight` for a closing "the numbers" section. */
    stats?: Stat[];
    /** Column count for the `highlight` + `items` grid — defaults to 2
     *  (the sharing/lane/country layouts); some guides need 3. */
    highlightColumns?: number;
};

export default function GuideSection({
    index,
    catnum,
    heading,
    intro,
    items,
    countries,
    tools,
    columns,
    day,
    children,
    photoPlaceholder,
    photo,
    accent,
    chipLabel,
    highlight,
    stats,
    highlightColumns,
}: Props) {
    const secnum = catnum ?? String(index).padStart(2, "0");
    const bandModifier =
        index % 2 === 0 ? "gt-guide-rail--cream" : "gt-guide-rail--surface";

    if (tools) {
        return (
            <section className={`gt-guide-rail ${bandModifier}`}>
                <div className="gt-guide-rail__inner">
                    <div className="gt-guide-rail__aside">
                        <span
                            className="gt-guide-rail__catnum"
                            style={{ background: accent?.fill }}
                        >
                            {secnum}
                        </span>
                        {chipLabel && (
                            <span
                                className="gt-guide-rail__chip"
                                style={{
                                    background: accent?.chipBg,
                                    color: accent?.chipText,
                                }}
                            >
                                {chipLabel}
                            </span>
                        )}
                        <h2 className="gt-guide-rail__heading">{heading}</h2>
                        {intro && <p className="gt-guide-rail__intro">{intro}</p>}
                    </div>
                    <div className="gt-guide-rail__items">
                        {tools.map((t) => (
                            <div key={t.name} className="gt-tool-row">
                                <div className="gt-tool-row__head">
                                    <h3 className="gt-tool-row__name">
                                        {t.url ? (
                                            <a
                                                href={t.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {t.name}
                                            </a>
                                        ) : (
                                            t.name
                                        )}
                                    </h3>
                                    <span className="gt-tool-row__cost">{t.cost}</span>
                                </div>
                                <p className="gt-tool-row__tagline">{t.tagline}</p>
                                <div className="gt-tool-row__facts">
                                    <div className="gt-tool-row__fact">
                                        <span className="gt-tool-row__lbl">Strengths</span>
                                        <p>{t.strengths}</p>
                                    </div>
                                    <div className="gt-tool-row__fact">
                                        <span className="gt-tool-row__lbl">Weakness</span>
                                        <p>{t.weaknesses}</p>
                                    </div>
                                    <div className="gt-tool-row__fact">
                                        <span className="gt-tool-row__lbl">Best for</span>
                                        <p>{t.bestFor}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    if (day) {
        return (
            <section className={`gt-guide-rail ${bandModifier}`}>
                <div className="gt-guide-rail__inner">
                    <div className="gt-guide-rail__aside">
                        <span
                            className="gt-guide-rail__catnum"
                            style={{ background: day.accent.fill }}
                        >
                            {day.catnum}
                        </span>
                        <span
                            className="gt-guide-rail__chip"
                            style={{
                                background: day.accent.chipBg,
                                color: day.accent.chipText,
                            }}
                        >
                            <span aria-hidden="true">{day.flag}</span> {day.place}
                        </span>
                        <h2 className="gt-guide-rail__heading">{day.title}</h2>
                        <p className="gt-guide-rail__intro">{day.summary}</p>
                    </div>
                    <div className="gt-guide-rail__day-content">{children}</div>
                </div>
            </section>
        );
    }

    if (countries) {
        return (
            <section className={`gt-guide-rail gt-guide-rail--countries ${bandModifier}`}>
                <div className="gt-guide-rail__inner gt-guide-rail__inner--full">
                    <div className="gt-guide-rail__head">
                        <span className="gt-guide-rail__secnum">{secnum}</span>
                        <h2 className="gt-guide-rail__heading">{heading}</h2>
                    </div>
                    {intro && <p className="gt-guide-country-intro">{intro}</p>}
                    <div className="gt-guide-country-grid">
                        {countries.map((c) => (
                            <article key={c.name} className="gt-guide-country-card">
                                <h4>
                                    <span
                                        className="gt-guide-country-card__flag"
                                        aria-hidden="true"
                                    >
                                        {c.flag}
                                    </span>
                                    {c.name}
                                </h4>
                                <p>{c.body}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    if (columns) {
        return (
            <section className={`gt-guide-rail ${bandModifier}`}>
                <div className="gt-guide-rail__inner gt-guide-rail__inner--columns">
                    {columns.map((col) => (
                        <div key={col.heading}>
                            <h2 className="gt-guide-rail__heading gt-guide-rail__heading--col">
                                {col.heading}
                            </h2>
                            <div className="gt-guide-rail__items">
                                {col.items.map((item) => (
                                    <div key={item.title} className="gt-guide-rail__item">
                                        <span
                                            className="gt-guide-rail__item-num"
                                            style={
                                                item.marker && item.marker.length > 1
                                                    ? { fontSize: 11 }
                                                    : undefined
                                            }
                                        >
                                            {item.marker}
                                        </span>
                                        <div>
                                            <h3 className="gt-guide-rail__item-title">
                                                {item.title}
                                            </h3>
                                            <p className="gt-guide-rail__item-body">
                                                {item.body}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        );
    }

    if (highlight) {
        return (
            <section className={`gt-guide-rail gt-guide-rail--countries ${bandModifier}`}>
                <div className="gt-guide-rail__inner gt-guide-rail__inner--full">
                    <div className="gt-guide-rail__head">
                        <span
                            className="gt-guide-rail__secnum"
                            style={{
                                background: highlight.background,
                                color: highlight.color,
                            }}
                        >
                            {highlight.glyph}
                        </span>
                        <h2 className="gt-guide-rail__heading">{heading}</h2>
                    </div>
                    {intro && <p className="gt-guide-country-intro">{intro}</p>}
                    {children}
                    {stats ? (
                        <div className="gt-guide-stats-grid">
                            {stats.map((s) => (
                                <div key={s.label} className="gt-guide-stats-grid__item">
                                    <div className="gt-guide-stats-grid__value">
                                        {s.value}
                                    </div>
                                    <div className="gt-guide-stats-grid__label">
                                        {s.label}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div
                            className="gt-guide-rail__items gt-guide-rail__items--grid"
                            style={
                                highlightColumns
                                    ? { gridTemplateColumns: `repeat(${highlightColumns}, 1fr)` }
                                    : undefined
                            }
                        >
                            {items?.map((item, i) => (
                                <div key={item.title} className="gt-guide-rail__item">
                                    <span
                                        className="gt-guide-rail__item-num"
                                        style={{
                                            ...(item.marker && item.marker.length > 1
                                                ? { fontSize: 17 }
                                                : undefined),
                                            ...(item.markerBg && {
                                                background: item.markerBg,
                                                color: item.markerColor,
                                            }),
                                        }}
                                    >
                                        {item.marker ?? i + 1}
                                    </span>
                                    <div>
                                        <h3 className="gt-guide-rail__item-title">
                                            {item.title}
                                        </h3>
                                        <p className="gt-guide-rail__item-body">
                                            {item.body}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>
        );
    }

    return (
        <section className={`gt-guide-rail ${bandModifier}`}>
            <div className="gt-guide-rail__inner">
                <div className="gt-guide-rail__aside">
                    {chipLabel ? (
                        <>
                            <span
                                className="gt-guide-rail__catnum"
                                style={{ background: accent?.fill }}
                            >
                                {secnum}
                            </span>
                            <span
                                className="gt-guide-rail__chip"
                                style={{
                                    background: accent?.chipBg,
                                    color: accent?.chipText,
                                }}
                            >
                                {chipLabel}
                            </span>
                            <h2 className="gt-guide-rail__heading">{heading}</h2>
                            {intro && (
                                <p className="gt-guide-rail__intro gt-guide-rail__intro--keep">
                                    {intro}
                                </p>
                            )}
                        </>
                    ) : (
                        <>
                            <div className="gt-guide-rail__head">
                                <span className="gt-guide-rail__secnum">{secnum}</span>
                                <h2 className="gt-guide-rail__heading">{heading}</h2>
                            </div>
                            {intro && <p className="gt-guide-rail__intro">{intro}</p>}
                        </>
                    )}
                    {photo && (
                        <img
                            className="gt-guide-rail__photo"
                            src={photo.src}
                            alt={photo.alt}
                        />
                    )}
                    {photoPlaceholder && (
                        <div className="gt-guide-rail__photo-placeholder">
                            <span>PHOTO — {photoPlaceholder}</span>
                        </div>
                    )}
                </div>
                <div className="gt-guide-rail__items">
                    {items?.map((item, i) => (
                        <div key={item.title} className="gt-guide-rail__item">
                            <span className="gt-guide-rail__item-num">
                                {item.marker ?? i + 1}
                            </span>
                            <div>
                                <h3 className="gt-guide-rail__item-title">
                                    {item.title}
                                </h3>
                                <p className="gt-guide-rail__item-body">{item.body}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
